import { dependencies } from '@pn/core/dependencies';
import type { DataItem, DataItemId } from '@pn/core/domain/data';
import { LayerType, type Layer, type LayerStyle } from '@pn/core/domain/layer';
import { getMapDataFields } from '@pn/core/domain/query';
import type { UnitSystem } from '@pn/core/domain/types';
import {
  generateSelectionStyle,
  isStreamableItem,
  type WorkspaceItem,
} from '@pn/core/domain/workspace';
import { assert } from '@pn/core/utils/assert';
import { formatDomainValue, getRawDomainValue } from '@pn/core/utils/format';
import { arraysShareSameValues } from '@pn/core/utils/logic';
import { isGeoJSONGeometry } from '@pn/services/utils/geojson';
import isEqual from 'fast-deep-equal';
import { isEmpty, isNil } from 'lodash-es';

const hiddenIds: Record<string, Record<string, Set<DataItemId>>> = {};

export function syncVectorFeaturesState(
  item: WorkspaceItem,
  mapDataItems: DataItem[] = []
): void {
  const { id, dataType } = item;

  assert(item.sourceItem, 'sourceItem is required for syncing');

  if (!hiddenIds[dataType]) hiddenIds[dataType] = {};

  hiddenIds[dataType][id] = new Set();
  mapDataItems.forEach(({ _id }) => hiddenIds[dataType][id].add(_id));

  const idsToHide: DataItemId[] = [];
  Object.values(hiddenIds[dataType]).forEach((ids) => {
    ids.forEach((id) => idsToHide.push(id));
  });

  const skipFiltering = isEmpty(idsToHide);

  item.sourceItem.map.layers.forEach((layer) => {
    dependencies.map.filterLayer({
      layerId: layer.id,
      key: 'hide_ids',
      filter: skipFiltering ? undefined : ['!in', 'internal_id', ...idsToHide],
    });
  });
}

const prevLayers = new Map<WorkspaceItem['id'], Layer[]>();

const layersParams = new Map<
  Layer['id'],
  {
    type: LayerType;
    style: LayerStyle;
    metadata: Layer['metadata'];
    mapDataFields: string[];
    mapDataItems: DataItem[];
    unitSystem: UnitSystem;
  }
>();

export function processLayers(params: {
  item: WorkspaceItem;
  beforeLayerId: string | undefined;
  mapDataItems: DataItem[];
}): void {
  const { item, beforeLayerId, mapDataItems } = params;
  const { map } = dependencies;

  assert(item.query.unitSystem, 'Unit system is required to process layers');

  const mapDataFields = getMapDataFields(item);

  for (let i = 0; i < item.map.layers.length; i++) {
    const layer = item.map.layers[i];

    const style = isStreamableItem(item)
      ? getFormattedStyle(layer.style, layer.type)
      : layer.style;
    const selectionStyle = generateSelectionStyle(style, layer.type);

    if (map.hasLayer(layer.id)) {
      const previousParams = layersParams.get(layer.id);

      const hasLayerTypeChanged = previousParams?.type !== layer.type;
      const hasMetadataChanged = !isEqual(
        previousParams?.metadata,
        layer.metadata
      );

      if (hasLayerTypeChanged || hasMetadataChanged) {
        map.removeDataLayer(layer);
        map.addDataLayer(layer, {
          beforeLayerId,
          sourceLayer: item.sourceItem?.map.layers[i],
          style,
          selectionStyle,
        });

        if (isGeoJsonLayer(layer)) {
          const data = generateLayerData({
            layerType: layer.type,
            mapDataItems,
            sourceField: layer.sourceField,
            unitSystem: item.query.unitSystem,
          });
          map.updateLayerData({ layer, data });
        }
      } else {
        const hasLayerStyleChanged = !isEqual(previousParams?.style, style);
        if (hasLayerStyleChanged) {
          map.updateDataLayerStyle(layer, {
            style,
            selectionStyle,
          });
        }
      }

      map.moveDataLayer(layer, beforeLayerId);

      const hasMapDataChanged =
        previousParams?.unitSystem !== item.query.unitSystem ||
        !arraysShareSameValues(previousParams?.mapDataFields, mapDataFields) ||
        haveIdsChanged(previousParams?.mapDataItems, mapDataItems);

      if (isGeoJsonLayer(layer) && hasMapDataChanged) {
        const data = generateLayerData({
          layerType: layer.type,
          mapDataItems,
          sourceField: layer.sourceField,
          unitSystem: item.query.unitSystem,
        });
        map.updateLayerData({ layer, data });
      }
    } else {
      map.addDataLayer(layer, {
        beforeLayerId,
        sourceLayer: item.sourceItem?.map.layers[i],
        style,
        selectionStyle,
      });

      if (isGeoJsonLayer(layer) && item.dataSource.type !== 'none') {
        const data = generateLayerData({
          layerType: layer.type,
          mapDataItems,
          sourceField: layer.sourceField,
          unitSystem: item.query.unitSystem,
        });
        map.updateLayerData({ layer, data });
      }
    }

    layersParams.set(layer.id, {
      type: layer.type,
      style,
      metadata: layer.metadata,
      mapDataFields,
      mapDataItems,
      unitSystem: item.query.unitSystem,
    });
  }

  prevLayers.get(item.id)?.forEach((layer) => {
    if (!item.map.layers.find((l) => l.id === layer.id)) {
      if (map.hasLayer(layer.id)) {
        map.removeDataLayer(layer);
      }
    }
  });

  prevLayers.set(item.id, item.map.layers);
}

export function removeLayers(layers: Layer[]): void {
  const { map } = dependencies;

  layers.forEach((layer) => {
    if (!map.hasLayer(layer.id)) return;

    map.removeDataLayer(layer);
  });
}

function isGeoJsonLayer(layer: Layer): boolean {
  return layer.source.type === 'geojson';
}

function haveIdsChanged(
  prevDataItems: DataItem[],
  newDataItems: DataItem[]
): boolean {
  if (prevDataItems.length !== newDataItems.length) {
    return true;
  }

  for (let i = 0; i < prevDataItems.length; i++) {
    if (prevDataItems[i]._id !== newDataItems[i]._id) {
      return true;
    }
  }

  return false;
}

function getFormattedKey(key: string): string {
  return `${key}:formatted`;
}

function generateLayerData(params: {
  layerType: LayerType;
  mapDataItems: DataItem[];
  sourceField?: string;
  unitSystem: UnitSystem;
}): DataItem[] {
  const { layerType, mapDataItems, sourceField, unitSystem } = params;

  const isTextLayer = layerType === LayerType.Text;

  const dataItems = mapDataItems.flatMap((item) => {
    const geometry = sourceField ? item[sourceField] : item.geometry;
    if (isNil(geometry) || !isGeoJSONGeometry(geometry)) return [];

    const transformedItem: DataItem = { _id: item._id, geometry };
    for (const key in item) {
      if (key === '_id' || key === 'geometry') continue;

      transformedItem[key] = getRawDomainValue(item[key]);
      if (isTextLayer) {
        transformedItem[getFormattedKey(key)] = formatDomainValue({
          value: item[key],
          unitSystem,
          displaySymbol: true,
        });
      }
    }

    return [transformedItem];
  });

  return dataItems;
}

/**
 * Modify the `field` property to fetch formatted values.
 */
function getFormattedStyle(
  style: LayerStyle,
  layerType: LayerType
): LayerStyle {
  return Object.entries(style).reduce<Record<string, unknown>>(
    (acc, [key, value]) => {
      if (layerType === LayerType.Text && key === 'field') {
        acc[key] = ['get', getFormattedKey((value as string[])[1])];
      } else {
        acc[key] = value;
      }
      return acc;
    },
    {}
  );
}
